import get from 'lodash/get';
import axiosConfig from '../../../../../redux/Spoolsbase/axiosConfig';
import type { QcSignaturePayload } from './components/weld/spoolStepPayloadHelpers';

/**
 * POST /spool-tracker/ncr/register
 * Register a workflow NCR with project / pipe / spool / weld / step context.
 * The HTTP call is performed by `registerSpoolNcrSaga` in `redux/Spoolsbase/saga.js`.
 * Align field names with backend when the contract is finalized.
 */
export const SPOOL_NCR_REGISTER_PATH = '/spool-tracker/ncr/register';

/** GET /spool-tracker/ncr?project_id=&pipe_id=[&spool_id=] */
export const SPOOL_NCR_LIST_PATH = '/spool-tracker/ncr';

export type SpoolTrackerWeldStepType =
  | 'fitup'
  | 'preheat'
  | 'welding'
  | 'repair_weld'
  | 'vt'
  | 'ndt'
  | 'coating'
  | 'accept';

export type SpoolNcrSubmissionMode = 'draft' | 'submit';

export interface SpoolWorkflowNcrRegisterPayload {
  projectId: string | number | null;
  pipeId: string | number | null;
  spoolId: number;
  /** Spool display name (e.g. card title). */
  spoolName: string;
  weldId: string | null;
  weldNumber: string | null;
  /** Source workflow step (API key). */
  weldStepType: SpoolTrackerWeldStepType;
  /** UI step label (e.g. “Visual (VT)”) — also sent as `category` for register grids. */
  categoryLabel: string;
  ncrNumber: string;
  /** ISO date string YYYY-MM-DD */
  ncrDate: string;
  description: string;
  disposition: 'pending' | 'rework' | 'accept' | 'reject' | 'repair';
  buyerNotified: 'no' | 'yes' | 'yes_date';
  status: 'open' | 'pending_closure' | 'closed';
  details: string;
  signature: QcSignaturePayload;
  submissionMode: SpoolNcrSubmissionMode;
}

/** One row in the NCR register table (UI + list API). */
export type NcrRow = {
  id: string;
  ncrNo: string;
  date: string;
  spool: string;
  category: string;
  description: string;
  /** Longer notes (from payload) when different from `description` */
  detailsText: string;
  disposition: string;
  dispositionVariant: 'draft' | 'warn' | 'pass';
  buyerNotified: string;
  status: string;
  statusVariant: 'fail' | 'warn' | 'pass';
  rootCause: string;
  correctiveAction: string;
  dispositionSelect: string;
  /** For POST /ncr/register updates (from list DTO) */
  spoolId: number;
  ncrDateRaw: string;
  /** Weld joint id; omit from register body when null */
  weldIdNum: number | null;
  weldStepType: SpoolTrackerWeldStepType;
  /** From row `submission_mode` — filter “submitted only” lists */
  ncrSubmissionMode: 'submit' | 'draft';
};

function normalizeWeldStepKey(s: string | undefined | null): string {
  return String(s ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Optional narrowing after weld scope: workflow step and/or `category` column (display / API text).
 */
export type FilterNcrRowsByWeldIdOptions = {
  /** When set, only rows whose `weldStepType` matches this step (same as workflow NCR table). */
  matchStepType?: SpoolTrackerWeldStepType | null;
  /**
   * When set, `row.category` (trim, case-insensitive) must **include** this substring, or **equal** it.
   * Use with or without `matchStepType` when the list DTO is keyed by display category.
   */
  matchCategoryText?: string | null;
};

/**
 * Scopes a flat NCR list to the active joint (GET returns all NCRs for the pipe).
 * `weldId` is a string (e.g. from `WeldJoint.id`); `weldIdNum` is numeric in the DTO.
 * Legacy rows with no `weldIdNum` are kept (same rule as per-step table filters).
 * Optionally also filter by workflow step and/or a substring of `category`.
 */
export function filterNcrRowsByWeldId(
  rows: NcrRow[],
  weldId: string | null | undefined,
  options?: FilterNcrRowsByWeldIdOptions | null
): NcrRow[] {
  let out = rows;
  if (weldId != null && String(weldId).trim() !== '') {
    const want = String(weldId);
    out = out.filter((r) => {
      if (r.weldIdNum == null) return true;
      return String(r.weldIdNum) === want;
    });
  }
  if (options?.matchStepType != null && String(options.matchStepType).trim() !== '') {
    const w = normalizeWeldStepKey(options.matchStepType);
    out = out.filter((r) => normalizeWeldStepKey(r.weldStepType) === w);
  }
  if (options?.matchCategoryText != null && String(options.matchCategoryText).trim() !== '') {
    const needle = String(options.matchCategoryText).trim().toLowerCase();
    out = out.filter((r) => {
      const c = (r.category ?? '').trim().toLowerCase();
      return c === needle || c.includes(needle);
    });
  }
  return out;
}

/**
 * NCRs raised from a specific workflow step for the current spool, optionally the current weld.
 * `onlySubmitted: true` returns rows where the server stored `submission_mode` is `submit`.
 */
export function filterNcrListForWeldPanel(
  rows: NcrRow[],
  options: { step: SpoolTrackerWeldStepType; weldId?: string | null; onlySubmitted: boolean }
): NcrRow[] {
  const want = normalizeWeldStepKey(options.step);
  return rows.filter((r) => {
    if (normalizeWeldStepKey(r.weldStepType) !== want) return false;
    if (options.onlySubmitted && r.ncrSubmissionMode !== 'submit') return false;
    if (options.weldId != null && String(options.weldId).trim() !== '') {
      if (r.weldIdNum != null && String(r.weldIdNum) !== String(options.weldId)) {
        return false;
      }
    }
    return true;
  });
}

function dispositionToVariant(
  d: string
): { label: string; variant: NcrRow['dispositionVariant'] } {
  const s = d.trim().toLowerCase();
  if (s === 'rework' || s.includes('rework')) return { label: 'Rework', variant: 'warn' };
  if (s === 'repair' || s.includes('repair')) return { label: 'Repair', variant: 'warn' };
  if (s === 'accept' || s.includes('accept as')) return { label: 'Accept As-Is', variant: 'pass' };
  if (s === 'reject') return { label: 'Reject', variant: 'warn' };
  if (s && s !== 'pending') return { label: d, variant: 'warn' };
  return { label: d || 'Pending', variant: 'draft' };
}

function statusToVariant(
  s: string
): { label: string; variant: NcrRow['statusVariant'] } {
  const t = s.trim().toLowerCase();
  if (t === 'closed' || t === 'close') return { label: 'Closed', variant: 'pass' };
  if (t === 'pending_closure' || (t.includes('pending') && t.includes('closure'))) {
    return { label: 'Pending Closure', variant: 'warn' };
  }
  if (t === 'open' || t === '') return { label: 'Open', variant: 'fail' };
  return { label: s, variant: 'fail' };
}

function formatNcrListDate(v: unknown): string {
  if (v == null) return '—';
  const str = String(v).trim();
  if (!str) return '—';
  const t = Date.parse(str);
  if (Number.isNaN(t)) return str;
  return new Date(t).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function extractNcrArray(resBody: unknown): Record<string, unknown>[] {
  if (Array.isArray(resBody)) return resBody as Record<string, unknown>[];
  const nest = resBody && typeof resBody === 'object' ? (resBody as Record<string, unknown>) : {};
  const d1 = nest.data;
  if (Array.isArray(d1)) return d1 as Record<string, unknown>[];
  if (d1 && typeof d1 === 'object' && !Array.isArray(d1)) {
    const inner = d1 as Record<string, unknown>;
    if (Array.isArray(inner.data)) {
      return inner.data as Record<string, unknown>[];
    }
    // Backend: GET /spool-tracker/ncr → actionCompleteResponse → { data: { ncrList: [...] } }
    if (Array.isArray(inner.ncrList)) {
      return inner.ncrList as Record<string, unknown>[];
    }
    if (Array.isArray(inner.list)) {
      return inner.list as Record<string, unknown>[];
    }
  }
  if (Array.isArray(nest.ncrList)) {
    return nest.ncrList as Record<string, unknown>[];
  }
  const cands = [
    nest.recordList,
    nest.ncrs,
    nest.list,
    (nest as { data?: { ncrs?: unknown; ncrList?: unknown } }).data?.ncrs,
    (nest as { data?: { ncrList?: unknown } }).data?.ncrList,
  ].filter(Boolean);
  for (const c of cands) {
    if (Array.isArray(c)) return c as Record<string, unknown>[];
  }
  return [];
}

/**
 * Spool NCR list rows: top-level DTO (id, ncrNumber, …) plus a JSON `payload` with the
 * original POST /ncr/register body (description, spoolName, categoryLabel, etc.).
 */
function ncrListRowForUi(raw: Record<string, unknown>): Record<string, unknown> {
  const p = raw.payload;
  if (p && typeof p === 'object' && p !== null) {
    return { ...(p as Record<string, unknown>), ...raw };
  }
  return raw;
}

/** Map one API/JSON row to `NcrRow` (camelCase + snake_case tolerant). */
export function mapApiRecordToNcrRow(raw: Record<string, unknown>, index: number): NcrRow {
  const row = ncrListRowForUi(raw);
  const id = String(
    row.id ?? row.ncr_id ?? row.ncrId ?? `ncr-${index}`
  );
  const ncrNumberStr = String(
    row.ncr_number ??
      row.ncrNumber ??
      row.ncrNo ??
      (row as { NCRNo?: string }).NCRNo ??
      '—'
  );
  const dateRaw =
    row.date ?? row.ncr_date ?? row.ncrDate ?? row.created_at ?? row.createdAt ?? '';
  const spool = String(
    row.spool_name ?? row.spoolName ?? row.spool ?? '—'
  );
  const category = String(
    row.category_label ??
      row.categoryLabel ??
      row.category ??
      row.weld_step_type ??
      row.weldStepType ??
      '—'
  );
  const description = String(row.description ?? row.notes ?? '');
  const detailsText = String(row.details ?? (row as { detailsText?: unknown }).detailsText ?? '');
  const dispRaw = String(
    row.disposition ?? row.disposition_select ?? row.dispositionSelect ?? 'pending'
  );
  const { label: dispLabel, variant: dispositionVariant } = dispositionToVariant(dispRaw);
  const buyer = String(
    row.buyer_notified ?? row.buyerNotified ?? row.buyer_notified_label ?? 'No'
  );
  const statusRaw = String(
    row.status ?? row.ncr_status ?? row.ncrStatus ?? 'open'
  );
  const { label: statusLabel, variant: statusVariant } = statusToVariant(statusRaw);
  const rootCause = String(
    row.root_cause ?? row.rootCause ?? ''
  );
  const correctiveAction = String(
    row.corrective_action ?? row.correctiveAction ?? ''
  );
  const spoolIdRaw = row.spool_id ?? row.spoolId;
  const spoolIdNum =
    typeof spoolIdRaw === 'number'
      ? spoolIdRaw
      : spoolIdRaw != null
        ? Number(String(spoolIdRaw))
        : NaN;
  const wRaw = row.weldId ?? row.weld_joint_id;
  let weldIdNum: number | null = null;
  if (wRaw != null && wRaw !== '') {
    const n = typeof wRaw === 'number' ? wRaw : Number(String(wRaw).trim());
    if (Number.isInteger(n) && n > 0) weldIdNum = n;
  }
  const wst = row.weldStepType ?? row.weld_step_type;
  const ncrDateRaw = String(
    dateRaw && String(dateRaw).trim() !== ''
      ? dateRaw
      : (row as { ncrDate?: string }).ncrDate != null
        ? String((row as { ncrDate?: string }).ncrDate)
        : new Date().toISOString().slice(0, 10)
  ).slice(0, 10);
  const weldStepType: SpoolTrackerWeldStepType = (() => {
    const w = String(wst ?? 'fitup')
      .trim()
      .toLowerCase() as string;
    const set: SpoolTrackerWeldStepType[] = [
      'fitup',
      'preheat',
      'welding',
      'repair_weld',
      'vt',
      'ndt',
      'coating',
      'accept',
    ];
    return (set as string[]).includes(w) ? (w as SpoolTrackerWeldStepType) : 'fitup';
  })();
  const subRaw = String(row.submissionMode ?? row.submission_mode ?? '').trim().toLowerCase();
  /** Only treat as draft when the API is explicit. Missing/unknown → submit so saved NCRs still appear in "submitted only" views. */
  const ncrSubmissionMode: 'submit' | 'draft' =
    subRaw === 'draft' || subRaw === 'in_progress' || subRaw === 'saving' ? 'draft' : 'submit';
  return {
    id,
    ncrNo: ncrNumberStr,
    date: formatNcrListDate(dateRaw),
    spool,
    category,
    description,
    detailsText: detailsText.trim() ? detailsText : description,
    disposition: dispLabel,
    dispositionVariant,
    buyerNotified: buyer,
    status: statusLabel,
    statusVariant,
    rootCause,
    correctiveAction,
    dispositionSelect: normalizeDispositionSelectKey(dispRaw),
    spoolId: Number.isFinite(spoolIdNum) && spoolIdNum > 0 ? spoolIdNum : 0,
    ncrDateRaw,
    weldIdNum,
    weldStepType,
    ncrSubmissionMode,
  };
}

function normalizeDispositionSelectKey(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (s === 'pending' || !s) return 'pending';
  if (s.includes('accept')) return 'accept';
  if (s === 'rework') return 'rework';
  if (s === 'repair') return 'repair';
  if (s === 'reject') return 'reject';
  return 'pending';
}

export type NcrRegisterDetailAction = 'save' | 'notify_buyer';

/**
 * Body for `POST /spool-tracker/ncr/register` to update the selected NCR in the register (merge with existing stored payload on the server).
 */
export function buildNcrRegisterDetailPayload(
  row: NcrRow,
  projectId: string | number,
  pipeId: string | number,
  rootCause: string,
  correctiveAction: string,
  dispositionKey: string,
  action: NcrRegisterDetailAction
): Record<string, unknown> {
  if (row.spoolId == null || !Number.isFinite(row.spoolId) || row.spoolId <= 0) {
    throw new Error('NCR is missing spool id. Reload the list and try again.');
  }
  const idNum = parseInt(String(row.id), 10);
  if (!Number.isInteger(idNum) || idNum <= 0) {
    throw new Error('Invalid NCR id.');
  }
  const disp = normalizeDispositionSelectKey(dispositionKey) as
    | 'pending'
    | 'rework'
    | 'accept'
    | 'reject'
    | 'repair';
  const ncrDate = (row.ncrDateRaw && row.ncrDateRaw.trim() !== ''
    ? row.ncrDateRaw
    : new Date().toISOString().slice(0, 10)
  ).slice(0, 10);
  const out: Record<string, unknown> = {
    id: idNum,
    ncrId: idNum,
    projectId,
    pipeId,
    spoolId: row.spoolId,
    ncrNumber: row.ncrNo,
    ncrDate,
    spoolName: row.spool,
    categoryLabel: row.category,
    weldStepType: row.weldStepType,
    description: row.description,
    details: row.detailsText,
    rootCause: rootCause.trim(),
    root_cause: rootCause.trim(),
    correctiveAction: correctiveAction.trim(),
    corrective_action: correctiveAction.trim(),
    disposition: disp,
    submissionMode: action === 'notify_buyer' ? 'submit' : 'draft',
  };
  if (row.weldIdNum != null) {
    out.weldId = row.weldIdNum;
  }
  if (action === 'notify_buyer') {
    out.buyerNotified = 'yes';
    out.status = 'pending_closure';
  }
  return out;
}

/**
 * `GET /spool-tracker/ncr?project_id=&pipe_id=[&spool_id=]`
 */
export async function fetchSpoolNcrList(params: {
  projectId: string | number;
  pipeId: string | number;
  spoolId?: number | null;
}): Promise<NcrRow[]> {
  const { projectId, pipeId, spoolId } = params;
  const res = await axiosConfig.get(SPOOL_NCR_LIST_PATH, {
    params: {
      project_id: projectId,
      pipe_id: pipeId,
      ...(spoolId != null ? { spool_id: spoolId } : {}),
    },
  });
  const body = get(res, 'data', res.data);
  const list = extractNcrArray(body);
  return list.map((row, i) => mapApiRecordToNcrRow(row, i));
}
